import { DataAccess } from "../dataaccess/data.access";
import { Timeout } from "../decorators/timeout.decorator";
import { S25Datefilter } from "../modules/s25-dateformat/s25.datefilter.service";
import { EventFormOccurrenceUtil } from "../modules/s25-event-creation-form/occurrences/event.form.occurrence.util";
import { S25ItemI } from "../pojo/S25ItemI";
import { S25Util } from "../util/s25-util";
import { FlsService } from "./fls.service";
import { UserprefService } from "./userpref.service";
import { EventQuota } from "../pojo/EventQuota";
import { EventQuotaInspect } from "../pojo/EventQuotaInspect";
import { Cache, Invalidate } from "../decorators/cache.decorator";

function applyMultiselectStatus(
    arr: readonly (S25ItemI | EventQuota.QuotaScopeItem)[] | undefined,
    retVal: EventQuota.JsonOutput,
    prop: keyof Pick<
        EventQuota.JsonOutput,
        "organizations" | "organizationSearches" | "locations" | "eventTypes" | "secGroups"
    >,
    action: "add" | "remove",
): void {
    const status: string = action === "add" ? "new" : "del";
    if (!arr) return;

    arr.forEach((item) => {
        const targetArray = retVal[prop] as any[];
        const exists = S25Util.array.getByProp(targetArray, "itemId", item.itemId);
        if (exists) {
            exists._status = status;
        } else {
            targetArray.push({
                itemId: item.itemId,
                _status: status,
                itemTypeId: item.itemTypeId,
            });
        }
    });
}
function dowFromRaw(raw: EventQuota.RawData): string {
    return EventQuota.normalizeDow(raw.dow);
}

export class EventQuotaService {
    public static quotaTypes: EventQuota.Type[] = [
        { itemId: EventQuota.TypeIds.System, itemName: "User" }, // deprecated, "System"
        { itemId: EventQuota.TypeIds.Location, itemName: "User" }, // deprecated, "Location"
        { itemId: EventQuota.TypeIds.User, itemName: "User" },
        { itemId: EventQuota.TypeIds.Organization, itemName: "Organization" },
    ];

    public static typeIdToName(id: number): string | undefined {
        const type = S25Util.array.getByProp(EventQuotaService.quotaTypes, "itemId", id);
        return type?.itemName;
    }

    public static objToJson(quotas: EventQuota.Detailed | EventQuota.Detailed[]): EventQuota.JsonOutput[] {
        const quotasArray: EventQuota.Detailed[] = S25Util.array.forceArray(quotas);
        return (
            quotasArray.map((q: EventQuota.Detailed): EventQuota.JsonOutput => {
                const retVal: EventQuota.JsonOutput = {
                    _status: q.status || q.itemId ? "mod" : "new",
                    active: q.active,
                    quota_id: q.itemId,
                    quota_name: q.itemName,
                    comment: q.comment,
                    quota_type: Number(q.type?.itemId),
                    quantity: q.quantity || 0,
                    max_minutes: q.countByEvent ? -1 : q.maxTime ? S25Util.ISODurationToMinutes(q.maxTime) : undefined,
                    duration: EventQuota.durationForPayload(q.windowMode, q.duration),
                    duration_type: q.durationType,
                    group_objects: q.groupItems ? 1 : 0,
                    skip_existing: q.skipExisting ? 1 : 0,
                    count_by_event: q.countByEvent ? 1 : 0,
                    include_requests: q.includeRequests ? 1 : 0,
                    window_mode: EventQuota.normalizeWindowMode(q.windowMode),
                    quota_start_dt: EventQuota.serverDateFromForm(q.quotaStartDt),
                    quota_end_dt: EventQuota.serverDateFromForm(q.quotaEndDt),
                    dow: EventQuota.normalizeDow(q.dow) || undefined,
                    allowed_time_start: EventQuota.formatTimeForServer(q.allowedTimeStart) ?? undefined,
                    allowed_time_end: EventQuota.formatTimeForServer(q.allowedTimeEnd) ?? undefined,
                    organizations: q.organizations || [],
                    organizationSearches: q.organizationSearches || [],
                    locations: q.locations || [],
                    eventTypes: q.eventTypes || [],
                    secGroups: q.secGroups
                        ? q.secGroups.map((group: any) => {
                              return { itemId: group.itemId, _status: group.status || "est" };
                          })
                        : [],
                };

                // applyMultiselectStatus(q.addedItems, retVal, "itemIds", "add");
                // applyMultiselectStatus(q.removedItems, retVal, "itemIds", "remove");

                applyMultiselectStatus(q.addedLocations, retVal, "locations", "add");
                applyMultiselectStatus(q.removedLocations, retVal, "locations", "remove");

                applyMultiselectStatus(q.addedEventTypes, retVal, "eventTypes", "add");
                applyMultiselectStatus(q.removedEventTypes, retVal, "eventTypes", "remove");

                applyMultiselectStatus(q.addedOrgs, retVal, "organizations", "add");
                applyMultiselectStatus(q.removedOrgs, retVal, "organizations", "remove");

                applyMultiselectStatus(q.addedOrgSearches, retVal, "organizationSearches", "add");
                applyMultiselectStatus(q.removedOrgSearches, retVal, "organizationSearches", "remove");

                applyMultiselectStatus(q.addedGroups, retVal, "secGroups", "add");
                applyMultiselectStatus(q.removedGroups, retVal, "secGroups", "remove");

                return retVal;
            }) || []
        );
    }

    public static jsonToObj(quotas: EventQuota.RawData | EventQuota.RawData[]): EventQuota.Detailed[] {
        const quotasArray: EventQuota.RawData[] = S25Util.array.forceArray(quotas);
        return (
            quotasArray.map((q: EventQuota.RawData): EventQuota.Detailed => {
                const durationTypeName: string =
                    EventQuota.durationNameMap[q.duration_type] + (q.duration !== 1 ? "s" : "");
                const typeInfo: S25ItemI = {
                    itemId: q.quota_type,
                    itemName: EventQuotaService.typeIdToName(q.quota_type) || "Unknown",
                };

                return {
                    itemId: q.quota_id,
                    itemName: q.quota_name,
                    comment: q.comment,
                    active: q.active,
                    type: typeInfo,
                    quantity: q.quantity,
                    duration: q.duration,
                    maxTime: S25Util.minutesToISODuration(q.max_minutes) || "P0DT00H00M",
                    durationType: q.duration_type,
                    durationTypeName,
                    groupItems: !!q.group_objects,
                    skipExisting: !!q.skip_existing,
                    countByEvent: !!q.count_by_event,
                    includeRequests: !!q.include_requests,
                    windowMode: EventQuota.normalizeWindowMode(q.window_mode),
                    windowModeName: EventQuota.windowModeLabel(EventQuota.normalizeWindowMode(q.window_mode)),
                    quotaStartDt: q.quota_start_dt,
                    quotaEndDt: q.quota_end_dt,
                    dow: dowFromRaw(q),
                    allowedTimeStart: q.allowed_time_start,
                    allowedTimeEnd: q.allowed_time_end,
                    items: S25Util.array.forceArray(q.itemIds),
                    organizations: S25Util.array.forceArray(q.organizations),
                    organizationSearches: S25Util.array.forceArray(q.organizationSearches),
                    locations: S25Util.array.forceArray(q.locations),
                    eventTypes: S25Util.array.forceArray(q.eventTypes),
                    secGroups: S25Util.array.forceArray(q.secGroups),
                };
            }) || []
        );
    }

    @Timeout
    @Cache({ immutable: true, targetName: "EventQuotaService" })
    public static getAll(): Promise<EventQuota.Detailed[]> {
        return DataAccess.get(DataAccess.injectCaller("/event/quotas/list.json", "EventQuotasService.getAll")).then(
            (resp) => {
                const quotas = S25Util.array.forceArray(resp && resp.root && resp.root.quotas);
                return S25Util.isObject(quotas[0]) ? EventQuotaService.jsonToObj(quotas as EventQuota.RawData[]) : [];
            },
        );
    }

    @Timeout
    @Invalidate({ serviceName: "EventQuotaService", methodName: "getAll" })
    public static set(quotas: EventQuota.Detailed[]) {
        const payload = S25Util.array.forceArray(EventQuotaService.objToJson(quotas));
        return DataAccess.put(DataAccess.injectCaller("/event/quotas/edit.json", "EventQuotasService.set"), {
            root: { quotas: payload },
        });
    }

    @Timeout
    @Invalidate({ serviceName: "EventQuotaService", methodName: "getAll" })
    public static delete(quotaIds: number[]) {
        quotaIds = S25Util.array.forceArray(quotaIds);
        return DataAccess.delete(
            DataAccess.injectCaller(
                "/event/quotas/delete.json?quota_id=" + quotaIds.join("+"),
                "EventQuotasService.delete",
            ),
        );
    }

    @Timeout
    public static inspect(request: EventQuotaInspect.InspectRequest): Promise<EventQuotaInspect.InspectResponse> {
        const locationAssignments = S25Util.array.forceArray(request.locationAssignments);
        const dates = S25Util.array.forceArray(request.dates);
        const payloadRoot: Record<string, unknown> = {
            target_username: request.targetUsername,
            organization_id: request.organizationId,
            dates: dates.map((dateRow) => ({
                occ_id: dateRow.occId,
                start_dt: S25Util.date.toS25ISODateTimeStr(dateRow.startDt),
                end_dt: S25Util.date.toS25ISODateTimeStr(dateRow.endDt),
                rsrv_id: dateRow.rsrvId,
                event_id: dateRow.eventId,
                event_start_dt: dateRow.eventStartDt
                    ? S25Util.date.toS25ISODateTimeStr(dateRow.eventStartDt)
                    : undefined,
                event_end_dt: dateRow.eventEndDt ? S25Util.date.toS25ISODateTimeStr(dateRow.eventEndDt) : undefined,
            })),
        };
        if (locationAssignments.length) {
            payloadRoot.locations = locationAssignments.map((assignment) => ({
                room_id: assignment.roomId,
                occ_id: assignment.occId,
            }));
        }
        if (request.eventTypeId) {
            payloadRoot.event_type_id = request.eventTypeId;
        }
        const payload = { root: payloadRoot };

        return DataAccess.post(
            DataAccess.injectCaller("/event/quotas/inspect.json", "EventQuotaService.inspect"),
            payload,
        ).then((resp) => EventQuotaInspect.inspectResponseFromRoot(resp?.root));
    }

    @Timeout
    public static check(
        occurrences: any,
        locations?: any[],
        orgId?: number,
        eventTypeId?: number,
        eventContext?: { eventId?: number; eventStartDt?: Date; eventEndDt?: Date },
    ) {
        // create an event from "more actions" produces this situation
        occurrences = S25Util.array.forceArray(occurrences);
        locations = S25Util.array.forceArray(locations);

        return FlsService.getFls().then((fls): any[] | Promise<any[]> => {
            if (fls.QUOTA_OVERRIDE === "F" || occurrences.length === 0) {
                // Has quota override bail early
                return [];
            } else {
                const occIdCount = 0;
                orgId = S25Util.parseInt(orgId) || undefined;
                eventTypeId = S25Util.parseInt(eventTypeId) || undefined;
                const payloadRoot: Record<string, unknown> = {
                    organization_id: orgId,
                    locations: locations?.map((loc) => {
                        return { room_id: loc.itemId, occ_id: loc.occ_id };
                    }),
                    dates: occurrences
                        .filter((occ: any) => {
                            return occ.state ? occ.state.itemId != 99 : true;
                        })
                        .map((occ: any) => {
                            const startDt = occ.evStartDt ? EventFormOccurrenceUtil.getStartDt(occ) : occ.startDt; //If statement is purely for defense, at one point occ.startDt was sent. Not sure if that still happens - T
                            const endDt = occ.evEndDt ? EventFormOccurrenceUtil.getEndDt(occ) : occ.endDt;
                            const occId = occ.uuid || occIdCount; //default
                            return Object.assign(
                                {
                                    start_dt: S25Util.date.toS25ISODateTimeStr(startDt),
                                    end_dt: S25Util.date.toS25ISODateTimeStr(endDt),
                                },
                                occId && { occ_id: occId },
                                occ.rsrvId && { rsrv_id: occ.rsrvId },
                                // Persisted rsrv_id: let SQL resolve event_id (copy/new can have stale form event_id).
                                !occ.rsrvId && eventContext?.eventId && { event_id: eventContext.eventId },
                                eventContext?.eventStartDt && {
                                    event_start_dt: S25Util.date.toS25ISODateTimeStr(eventContext.eventStartDt),
                                },
                                eventContext?.eventEndDt && {
                                    event_end_dt: S25Util.date.toS25ISODateTimeStr(eventContext.eventEndDt),
                                },
                            );
                        }),
                };
                if (eventTypeId) {
                    payloadRoot.event_type_id = eventTypeId;
                }
                const payload = { root: payloadRoot };

                return S25Util.all({
                    post: DataAccess.post(
                        DataAccess.injectCaller("/event/quotas/check.json", "EventQuotaService.check"),
                        payload,
                    ),
                    dateTimeFormat: UserprefService.getS25DateTimeformat(),
                    dateFormat: UserprefService.getS25Dateformat(),
                })
                    .then((resp) => {
                        const dateTimeFormat = resp.dateTimeFormat;
                        const dateFormat = resp.dateFormat;
                        const post = resp.post && resp.post.root;

                        const orgDates = S25Util.array.forceArray(post && post.org_dates);
                        const userDates = S25Util.array.forceArray(post && post.user_dates);

                        const orgBlocked = EventQuotaService.checkMap(
                            orgDates.filter((item: any) => item.allowed === 0),
                            2,
                            dateTimeFormat,
                            dateFormat,
                        );
                        const userBlocked = EventQuotaService.checkMap(
                            userDates.filter((item: any) => item.allowed === 0),
                            3,
                            dateTimeFormat,
                            dateFormat,
                        );

                        return [].concat(userBlocked, orgBlocked);
                    })
                    .catch((e): [] => {
                        //don't want an error in this service to disrupt saves that should otherwise be allowed. err toward not enforcing a quota
                        console.error(e);
                        return [];
                    });
            }
        });
    }

    public static getQuotaMsg(
        quotas: EventQuota.CheckResultMessageContext | EventQuota.CheckResultMessageContext[] | undefined | null,
        quotaTypeIds?: Array<number | string>,
    ): string {
        return EventQuota.formatCheckResultsExceededMessage(quotas, quotaTypeIds);
    }

    public static checkMap(quotas: any, quotaTypeId: number, dateTimeFormat: any, dateFormat: any) {
        quotas = S25Util.array.forceArray(quotas);
        return quotas.map((item: any) => {
            const quota = EventQuotaService.jsonToObj([item])[0];
            quota.subjectTypeId = item.quota_type;
            const subject = S25Util.array.getByProp(EventQuotaService.quotaTypes, "itemId", item.quota_type);
            quota.subjectTypeName = subject && subject.itemName;
            const windowLabels = EventQuota.windowDateLabels(quota, dateFormat);
            quota.windowStartLabel = windowLabels.windowStartLabel;
            quota.windowEndLabel = windowLabels.windowEndLabel;
            return {
                quota: quota,
                occ: {
                    allowed: item.allowed,
                    allowed_time: item.allowed_time,
                    allowed_count: item.allowed_count,
                    uuid: item.occ_id,
                    rsrvId: item.rsrv_id,
                    startDt: S25Datefilter.transform(item.start_dt, dateTimeFormat),
                    endDt: S25Datefilter.transform(item.end_dt, dateTimeFormat),
                    object_id: item.object_id,
                    object_type_id: item.object_type_id,
                },
            };
        });
    }
}
